export type {
  CountType,
  CountMode,
  CountStatus,
  LineStatus,
  CountSource,
  Progress,
  SessionVariance,
  StockCountSession,
  SessionSummary,
  StockCountLine,
  StockCountLinesMeta,
  StockCountReport,
  StockCountActivityItem,
  CreateStockCountPayload,
  UpdateStockCountLinePayload,
  SubmitStockCountPayload,
  ApproveStockCountPayload,
} from '../../../data/api/stockCountApiClient';

import type {
  CountMode,
  CountStatus,
  CountType,
  StockCountLine,
  StockCountSession,
} from '../../../data/api/stockCountApiClient';

export type StockCountView =
  | 'list'
  | 'wizard'
  | 'count'
  | 'review'
  | 'report';

export type WizardStep = 1 | 2 | 3 | 4 | 5 | 'freeze';

export function countTypeLabel(type: CountType): string {
  switch (type) {
    case 'full':
      return 'Complet';
    case 'cycle':
      return 'Cyclique';
    case 'spot':
      return 'Ponctuel';
    default:
      return type;
  }
}

export function countModeLabel(mode: CountMode): string {
  switch (mode) {
    case 'total_only':
      return 'Total seul';
    case 'dual':
      return 'Stock + Exposé';
    case 'floor_only':
      return 'Exposé seul';
    default:
      return mode;
  }
}

export function countStatusLabel(status: CountStatus): string {
  switch (status) {
    case 'draft':
      return 'Brouillon';
    case 'in_progress':
      return 'En cours';
    case 'review':
      return 'Revue';
    case 'closed':
      return 'Clôturé';
    case 'cancelled':
      return 'Annulé';
    default:
      return status;
  }
}

export function isActiveStatus(status: CountStatus): boolean {
  return status === 'in_progress' || status === 'review';
}

/** Mode-aware primary variance: floor_only compares exposé; other modes compare stock. */
export function primaryVariance(
  line: StockCountLine,
  countMode: CountMode,
): number | null {
  return countMode === 'floor_only' ? line.varianceExposed : line.varianceStock;
}

export function primaryTheoretical(line: StockCountLine, countMode: CountMode): number {
  return countMode === 'floor_only' ? line.theoreticalExposed : line.theoreticalStock;
}

export function isHighVariance(
  line: StockCountLine,
  countMode: CountMode,
  thresholdUnits: number,
  thresholdPercent: number,
): boolean {
  const variance = primaryVariance(line, countMode);
  if (variance == null) return false;
  const theoretical = primaryTheoretical(line, countMode);
  if (Math.abs(variance) > thresholdUnits) return true;
  if (theoretical !== 0) {
    const pct = Math.abs((variance / theoretical) * 100);
    if (pct > thresholdPercent) return true;
  }
  return false;
}

export function formatSessionTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString('fr-TN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function findActiveSession(
  sessions: StockCountSession[],
): StockCountSession | undefined {
  return sessions.find((s) => isActiveStatus(s.status));
}
